import { postData } from '../services';
import { rememberAndTrackDesignCreated } from '../services/portalAnalytics';
import { parseDesignIntake } from './buildKonfigearDesignLink';

export const PRODUCT_STANDARD = 'standard-beam';
export const PRODUCT_ECO = 'eco-beam';

export const PRODUCT_DESCRIPTIONS = {
    [PRODUCT_STANDARD]:
        "Includes all components except for a steel post. It's ready to assemble as packaged. It's the most customizable option and offers peak performance and features.",
    [PRODUCT_ECO]:
        'A simplified, core component for a durable, high-economy solar mount. Select parts are excluded and can be sourced locally, including the steel post, I-beams, and rail package.',
};

export const ECO_BEAM_PRESETS = {
    frame_duty_override: 'HD',
    pole_spacing_override: '8in Sch 40',
};

/** Auto-Designer query value for Eco-Beam (distinct from intake id `eco-beam`). */
export const AUTODESIGNER_PRODUCT_TYPE_ECO = 'EcoBeam';

/** Trailing " v2", " v3", … on a design name (case-insensitive). */
const DESIGN_NAME_VERSION_SUFFIX = /\s+v(\d+)$/i;

/**
 * Split a design name into its base label and version number.
 * Names without a suffix are treated as version 1.
 */
export const parseDesignNameVersion = (name) => {
    const trimmed = String(name || '').trim();
    if (!trimmed) {
        return { base: '', version: 1 };
    }
    const match = trimmed.match(DESIGN_NAME_VERSION_SUFFIX);
    if (!match) {
        return { base: trimmed, version: 1 };
    }
    return {
        base: trimmed.slice(0, match.index).trim(),
        version: parseInt(match[1], 10) || 1,
    };
};

/**
 * Next iterated name for a source design, e.g. "Foo" → "Foo v2", "Foo v2" → "Foo v3".
 * Scans existing project names that share the same base so the suffix stays unique.
 */
export const nextIteratedDesignName = (sourceName, existingNames = []) => {
    const { base } = parseDesignNameVersion(sourceName);
    if (!base) {
        return '';
    }

    const baseKey = base.toLowerCase();
    let maxVersion = 1;
    for (const name of existingNames) {
        const parsed = parseDesignNameVersion(name);
        if (parsed.base.toLowerCase() === baseKey) {
            maxVersion = Math.max(maxVersion, parsed.version);
        }
    }

    return `${base} v${maxVersion + 1}`;
};

export const buildDesignIntakePayload = (formData, isEcoBeam) => {
    const autodesignerOverrides = isEcoBeam
        ? ECO_BEAM_PRESETS
        : {
              frame_duty_override: 'Auto',
              pole_spacing_override: formData.pole_spacing_override,
          };

    return {
        ...formData,
        design_name: String(formData.design_name).trim(),
        frame_duty_override: autodesignerOverrides.frame_duty_override,
        pole_spacing_override: autodesignerOverrides.pole_spacing_override,
        beam_series: formData.product_type,
        product_description: PRODUCT_DESCRIPTIONS[formData.product_type] || '',
    };
};

export const buildAutoDesignerUrl = ({ designId, userEmail, project, intakePayload }) => {
    const designName = intakePayload.design_name || '';
    const addressParts = [
        project?.site_address_1,
        project?.site_address_2,
        [project?.city, project?.state, project?.zip_code].filter(Boolean).join(', '),
    ].filter(Boolean);
    const siteAddressRaw = addressParts.length > 0 ? addressParts.join(', ') : 'unknown';

    const baseParams = `userId=${designId}&userEmail=${userEmail || ''}&site_address=${siteAddressRaw}&project_id=${String(designName)}`;
    const intakeParams = new URLSearchParams({
        projectName: String(designName),
        beam_series: String(intakePayload.beam_series),
        frame_duty_override: String(intakePayload.frame_duty_override),
        number_columns: String(intakePayload.number_columns),
        number_rows: String(intakePayload.number_rows),
        tilt_angle: String(intakePayload.tilt_angle),
        pole_spacing_override: String(intakePayload.pole_spacing_override),
        module_width: String(intakePayload.module_width),
        module_length: String(intakePayload.module_length),
        ground_clearance: String(intakePayload.ground_clearance),
        exposure_category: String(intakePayload.exposure_category),
        pole_mount_quantity: String(intakePayload.pole_mount_quantity),
    });

    const isEcoBeam =
        intakePayload.beam_series === PRODUCT_ECO ||
        intakePayload.product_type === PRODUCT_ECO;
    if (isEcoBeam) {
        intakeParams.set('product_type', AUTODESIGNER_PRODUCT_TYPE_ECO);
    }

    const intakeQuery = intakeParams.toString();
    const queryString = intakeQuery ? `${baseParams}&${intakeQuery}` : baseParams;
    return `https://platform.skyciv.com/mt-solar?${queryString}`;
};

/**
 * Reconstructs the intake inputs used to build a design, preferring the raw
 * stored intake payload and falling back to the SkyCiv `file_data` snapshot
 * for older designs that predate that field.
 */
export const buildIntakeFormDataFromDesign = (sourceDesign) => {
    const intake = parseDesignIntake(sourceDesign) || {};
    const fileData = sourceDesign?.sky_civ_details?.file_data || {};

    return {
        product_type:
            intake.beam_series ||
            intake.product_type ||
            sourceDesign?.product_type ||
            sourceDesign?.beam_series ||
            PRODUCT_STANDARD,
        number_columns: intake.number_columns ?? fileData.number_columns ?? 3,
        number_rows: intake.number_rows ?? fileData.number_rows ?? 4,
        tilt_angle: intake.tilt_angle ?? fileData.tilt_angle ?? 50,
        pole_spacing_override: intake.pole_spacing_override ?? fileData.pole_override ?? 'auto',
        module_width: intake.module_width ?? fileData.module_width ?? 40,
        module_length: intake.module_length ?? fileData.module_length ?? 65,
        ground_clearance: intake.ground_clearance ?? fileData.ground_clearance ?? 5,
        exposure_category: intake.exposure_category ?? fileData.exposure_category ?? 'C',
        pole_mount_quantity: sourceDesign?.quantity || 1,
    };
};

/**
 * Creates a new design that reuses a source design's inputs under an
 * auto-incremented name (e.g. "Foo v2" → "Foo v3"), skipping the intake form
 * entirely, then opens the Auto-Designer once the server confirms creation.
 */
export const createIteratedDesign = async ({
    token,
    base_url,
    project,
    sourceDesign,
    existingNames = [],
    userEmail,
    setDesigns,
}) => {
    const designName = nextIteratedDesignName(sourceDesign?.design_name, existingNames);
    if (!designName) {
        throw new Error('Could not determine a name for the new design.');
    }

    const formData = {
        ...buildIntakeFormDataFromDesign(sourceDesign),
        design_name: designName,
    };

    return createDesignFromIntake({
        token,
        base_url,
        project,
        formData,
        runAutoDesigner: true,
        userEmail,
        setDesigns,
    });
};

export const createDesignFromIntake = async ({
    token,
    base_url,
    project,
    formData,
    runAutoDesigner,
    userEmail,
    setDesigns,
    deferAutoDesignerOpen = false,
}) => {
    const isEcoBeam = formData.product_type === PRODUCT_ECO;
    const intakePayload = buildDesignIntakePayload(formData, isEcoBeam);

    const submission = await postData(token, `${base_url}/apis/designs/create-design`, {
        project_id: project?.id,
        ...intakePayload,
    });

    if (submission?.error) {
        throw new Error(submission.error || 'Failed to create design. Please try again later.');
    }

    const createdDesign = submission?.design;
    const designId = createdDesign?.id;
    if (!designId) {
        throw new Error('Design was created but no design ID was returned. Please try again.');
    }

    rememberAndTrackDesignCreated(
        createdDesign,
        {
            companyId: project?.company_id,
            projectId: project?.id,
        },
        { fireIfAlreadyAssigned: true }
    );

    if (setDesigns) {
        setDesigns((prev) => {
            if (prev.some((d) => d.id === designId)) return prev;
            return [createdDesign, ...prev];
        });
    }

    const shouldOpenAutoDesigner = !isEcoBeam || runAutoDesigner;
    if (shouldOpenAutoDesigner) {
        const designName = createdDesign?.design_name || intakePayload.design_name;
        const url = buildAutoDesignerUrl({
            designId,
            userEmail,
            project,
            intakePayload: {
                ...intakePayload,
                design_name: designName,
            },
        });

        const title = designName ? `Auto-Designer — ${designName}` : 'Auto-Designer';
        const launchAutoDesigner = () => {
            if (typeof window.openAutoDesigner === 'function') {
                window.openAutoDesigner(url, { title });
            } else {
                window.open(url, '_blank', 'noreferrer');
            }
        };

        // Defer when opening from the intake modal so it can close first.
        if (deferAutoDesignerOpen) {
            requestAnimationFrame(launchAutoDesigner);
        } else {
            launchAutoDesigner();
        }
    }

    return createdDesign;
};
